import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignStatusModel } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignStatuses']

type CampaignStatusFilters = {
  string?: string,
}

type UseCampaignStatusesOptions = {
  filters?: CampaignStatusFilters,
  performHttpRequests?: boolean,
}

// @ts-expect-error TS1015
function useCampaignStatuses(options?: UseCampaignStatusesOptions = {}) {
  const { filters = {}, performHttpRequests } = options
  const {
    string: filterString,
  } = filters

  const {
    updatedEntities: { campaignStatuses: campaignStatusesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignStatuses } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignStatuses = useMemo(() => {
    const filtered = Object.values(campaignStatuses).filter((campaignStatus: CampaignStatusModel) => {
      const { id, title } = campaignStatus

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [campaignStatusesUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignStatusesCount = filteredCampaignStatuses.length
  const hasFilteredCampaignStatuses = !!filteredCampaignStatusesCount

  const { loading: loadingCampaignStatuses } = useReduxAction(
    'campaignStatuses',
    'loadCampaignStatuses',
    {
      ...filters,
    },
    [performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  return {
    campaignStatuses,
    filteredCampaignStatuses,
    filteredCampaignStatusesCount,
    hasFilteredCampaignStatuses,
    loading: loadingCampaignStatuses,
  }
}

export default useCampaignStatuses
